import { httpClient } from "@/services/httpClient";
import { logger } from "@/services/loggerService";
/**
 * Emit one anonymous funnel event. Fire-and-forget: resolves on 202, and on ANY
 * failure (network, 4xx, 5xx) swallows the error after a value-free `logger.debug`
 * — it NEVER rejects, so callers can `void recordEvent(...)` without a catch and the
 * donor UI is never affected by an analytics hiccup.
 */
export async function recordEvent(slug, body) {
    try {
        await httpClient.post(`/public/forms/${slug}/events`, body);
    }
    catch {
        // Silent by design (the brief): analytics failure must not toast or error.
        // R47 — a silent catch logs; a beacon miss is diagnostic, so debug (not error).
        // R73 — value-free context: the event name + slug only, never the anonymous token
        // nor any donor field.
        logger.debug("Funnel event not recorded", { slug, eventType: body.eventType });
    }
}
/**
 * Emit a funnel event RELIABLY at navigation time via `navigator.sendBeacon`.
 *
 * `RedirectedToPayment` fires immediately before a top-level OPAY redirect
 * (`window.top.location.assign`), so a plain async `recordEvent` (axios) is almost always
 * CANCELLED by the page unload — the funnel's key conversion step would be silently lost.
 * `sendBeacon` hands the request to the browser to deliver out-of-band even as the page
 * navigates away. Same NEUTRAL public path (R83), same value-free body, same fire-and-forget
 * contract (never throws, never blocks). Falls back to the best-effort axios `recordEvent`
 * only when sendBeacon is unavailable or refuses the payload (queue full / too large).
 */
export function recordEventBeacon(slug, body) {
    const base = httpClient.defaults.baseURL ?? "/api";
    const url = `${base}/public/forms/${slug}/events`;
    try {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
            // Same-origin endpoint, so the application/json Blob triggers no CORS preflight.
            if (navigator.sendBeacon(url, blob)) {
                return;
            }
        }
    }
    catch {
        // sendBeacon threw (e.g. blocked by policy) — fall through to the best-effort path.
    }
    // sendBeacon unavailable or refused — best-effort POST (may still race the navigation).
    void recordEvent(slug, body);
}
